/**
 * Centralized date/time formatting utilities.
 * Consolidates formatting helpers previously scattered across
 * AddContentPanel, SiteStatsPanel, ManageUsersPanel, and AppSidebar.
 */

import i18n from '../i18n';

/**
 * Format a duration in milliseconds to a human-readable string.
 * Examples: "3s", "1m 24s", "2h 5m 12s"
 */
export function formatDuration(ms: number): string {
  if (ms < 0) return '\u2014';
  const totalSeconds = Math.floor(ms / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  if (hours > 0) {
    return `${hours}h ${minutes}m ${seconds}s`;
  }
  if (minutes > 0) {
    return `${minutes}m ${seconds}s`;
  }
  return `${seconds}s`;
}

/**
 * Format an ISO date string to a short readable date (e.g. "Feb 14, 2026").
 */
export function formatDate(iso: string | null): string {
  if (!iso) return '\u2014';
  try {
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return iso;
  }
}

/**
 * Compact history/content-table timestamp in the Figma "HH:mm | D Mon YYYY"
 * shape, e.g. "15:16 | 1 Oct 2026" / "01:11 | 21 Mar 2026". 24-hour,
 * zero-padded hour, rendered in the viewer's local time.
 *
 * Uses the `en-GB` locale so the day-month-year ordering, English short month,
 * and 24h padding match the Figma spec regardless of the viewer's locale
 * (e.g. `en-US` would otherwise render "Oct 1, 2026"). The time zone is still
 * the viewer's local one — only the field ordering/formatting is pinned.
 */
export function formatHistoryTimestamp(iso: string | null): string {
  if (!iso) return '\u2014';
  try {
    const d = new Date(iso);
    const time = d.toLocaleTimeString('en-GB', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });
    const datePart = d.toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
    return `${time} | ${datePart}`;
  } catch {
    return iso;
  }
}

/**
 * Compact indexed timestamp for content tables: "11:03 | 23 Mar 2026".
 * Alias of {@link formatHistoryTimestamp} — identical "HH:mm | D Mon YYYY" shape.
 */
export const formatIndexedAtList = formatHistoryTimestamp;

/**
 * Format an ISO date string to date + time (e.g. "Feb 14, 2026, 3:45 PM").
 */
export function formatDateTime(value: string | number | null): string {
  if (value == null) return '\u2014';
  try {
    const d = new Date(value);
    return d.toLocaleString(undefined, {
      dateStyle: 'medium',
      timeStyle: 'short',
    });
  } catch {
    return String(value);
  }
}

/**
 * Format an ISO date string to a relative time string (e.g. "5m ago", "Yesterday").
 */
export function formatRelativeTime(isoString: string): string {
  try {
    const date = new Date(isoString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMin = Math.floor(diffMs / 60_000);
    const diffHr = Math.floor(diffMs / 3_600_000);
    const diffDay = Math.floor(diffMs / 86_400_000);

    if (diffMin < 1) return i18n.t('components.relativeTime.justNow');
    if (diffMin < 60) return i18n.t('components.relativeTime.compactMinutes', { num: diffMin });
    if (diffHr < 24) return i18n.t('components.relativeTime.compactHours', { num: diffHr });
    if (diffDay === 1) return i18n.t('components.relativeTime.yesterday');
    if (diffDay < 7) return i18n.t('components.relativeTime.compactDays', { num: diffDay });
    return date.toLocaleDateString(i18n.language || undefined);
  } catch {
    return '';
  }
}

/**
 * Relative time for chat history list rows (e.g. "19 mins. ago", "2 hrs. ago").
 */
export function formatChatHistoryRelativeTime(isoString: string): string {
  try {
    const date = new Date(isoString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMin = Math.floor(diffMs / 60_000);
    const diffHr = Math.floor(diffMs / 3_600_000);
    const diffDay = Math.floor(diffMs / 86_400_000);

    if (diffMin < 1) return i18n.t('components.relativeTime.justNow');
    if (diffMin < 60) return i18n.t('components.relativeTime.verboseMinutes', { count: diffMin });
    if (diffHr < 24) return i18n.t('components.relativeTime.verboseHours', { count: diffHr });
    if (diffDay === 1) return i18n.t('components.relativeTime.yesterday');
    if (diffDay < 7) return i18n.t('components.relativeTime.verboseDays', { num: diffDay });
    return date.toLocaleDateString(i18n.language || undefined, { month: 'short', day: 'numeric' });
  } catch {
    return '';
  }
}
